// @flow

import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled, { type StyledComponent } from 'styled-components';
import { __ } from '../../../i18n/l10n';
import { colorPalette, sizes, fontFamily } from '../../../styles/GlobalStyle';
import { InputText } from './InputText';

type DatePart = 'day' | 'month' | 'year';

type DateData = {
  day: string,
  month: string,
  year: string,
};

type ActiveParts = {
  day: boolean,
  month: boolean,
  year: boolean,
};

type Format = 'default' | 'expiration';

type InputDateProps = {|
  className?: string,
  value: string,
  name: string,
  label: string,
  id: string,
  invalid?: boolean,
  register?: void,
  onChange?: (event: string) => any,
  checkDate?: (date: Date, dateStr?: string) => any,
  format?: Format,
  readOnlyField?: boolean,
|};

const InputDateRaw = ({
  className,
  value,
  name,
  label,
  id,
  invalid = false,
  register,
  onChange,
  checkDate,
  format = 'default',
  readOnlyField = false,
}: InputDateProps) => {
  const [formattedValue, setFormattedValue] = useState('');
  const [day, setDay] = useState(format === 'expiration' ? '02' : '');
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');
  const [active, setActive] = useState(value && value !== '');
  const activeParts: ActiveParts = { day: false, month: false, year: false };
  const inputDay = useRef<?HTMLInputElement>();
  const inputMonth = useRef<?HTMLInputElement>();
  const inputYear = useRef<?HTMLInputElement>();

  const generateDateStr = useCallback(
    (dateData: DateData) => {
      if (
        Number.isNaN(dateData.day) ||
        Number.isNaN(dateData.month) ||
        Number.isNaN(dateData.year) ||
        (format === 'expiration' && dateData.year.length !== 2)
      ) {
        return '';
      }

      const formattedDateData = {
        day: format === 'expiration' ? 2 : parseInt(dateData.day, 10),
        month: parseInt(dateData.month, 10) - 1,
        year: parseInt(format === 'expiration' ? `20${dateData.year}` : dateData.year, 10),
      };

      const dateUTC = new Date(
        Date.UTC(formattedDateData.year, formattedDateData.month, formattedDateData.day)
      );
      const timezoneOffset = dateUTC.getTimezoneOffset() * 60 * 1000; // convert to ms
      const timezoneDate = dateUTC.getTime() + timezoneOffset;
      const date = new Date(timezoneDate);

      if (
        // TOTHINK: this is the only way we found to check for a valid date
        // eslint-disable-next-line no-self-compare
        date.getTime() === date.getTime() &&
        date.getFullYear() === formattedDateData.year &&
        date.getMonth() === formattedDateData.month &&
        date.getDate() === formattedDateData.day
      ) {
        const dateStr = {
          year: date.getFullYear(),
          month: `0${date.getMonth() + 1}`.slice(-2),
          day: `0${date.getDate()}`.slice(-2),
        };

        const fullDateStr = `${dateStr.year}-${dateStr.month}-${dateStr.day}`;

        checkDate && checkDate(date, fullDateStr);

        return fullDateStr;
      }

      return '';
    },
    [format]
  );

  useEffect(() => {
    const dateStr = generateDateStr({ day, month, year });
    if (formattedValue !== dateStr) {
      setFormattedValue(dateStr);
    }
  }, [day, formattedValue, generateDateStr, month, year]);

  useEffect(() => {
    if (onChange) {
      onChange(formattedValue);
    }
  }, [formattedValue]);

  const updateActive = useCallback(
    (activeValue: boolean, activePart: DatePart) => {
      activeParts[activePart] = activeValue;
      setActive(activeParts.day || activeParts.month || activeParts.year);
    },
    [activeParts]
  );

  useEffect(() => {
    if (
      day &&
      day.length >= 2 &&
      inputMonth.current &&
      document.activeElement === inputDay.current
    ) {
      inputMonth.current.focus();
    }
  }, [day]);

  useEffect(() => {
    if (
      month &&
      month.length >= 2 &&
      inputYear.current &&
      document.activeElement === inputMonth.current
    ) {
      inputYear.current.focus();
    }
  }, [month]);

  useEffect(() => {
    if (year && year.length >= 4) {
      const birthCityInput = document.getElementById('birth_city');
      birthCityInput && birthCityInput.focus();
    }
  }, [year]);

  useEffect(() => {
    // If date value is not valid, the activeParts are set as invalid instead of inactive
    // If so, they appear as focused/active
    if (value && value !== '' && value !== 'Invalid date' && !active) {
      let [yearStr] = value.split('-');
      const [, monthStr, dayStr] = value.split('-');
      if (format === 'expiration') {
        yearStr = yearStr.substr(2);
      }

      dayStr && setDay(dayStr);
      monthStr && setMonth(monthStr);
      yearStr && setYear(yearStr);
    }
  }, [active, format, value]);

  return (
    <span className={`${className || ''}${active ? ' active' : ''}${invalid ? ' invalid' : ''}`}>
      <span className="top-label" htmlFor={id}>
        {label}
      </span>
      <span className="parts-wrapper">
        {format !== 'expiration' && (
          <InputText
            className="field day-part"
            type="tel"
            mask={{ regex: '\\d{0,2}', inputmode: 'tel' }}
            name={`${name}_day`}
            id="bday-day"
            label={__('!placeholder:dd')}
            value={day}
            ref={(e: any) => {
              inputDay.current = e;
              if (register) {
                register(e);
              }
            }}
            onChange={setDay}
            onActiveChange={(event) => {
              updateActive(event, 'day');
            }}
            readOnlyField={readOnlyField}
            displayLockedIcon={false}
          />
        )}
        <InputText
          className="field month-part"
          type="tel"
          mask={{ regex: '\\d{0,2}', inputmode: 'tel' }}
          name={`${name}_month`}
          id="bday-month"
          label={__('!placeholder:mm')}
          value={month}
          ref={(e: any) => {
            inputMonth.current = e;
            if (register) {
              register(e);
            }
          }}
          onChange={setMonth}
          onActiveChange={(event) => {
            updateActive(event, 'month');
          }}
          readOnlyField={readOnlyField}
          displayLockedIcon={false}
        />
        <InputText
          className="field year-part"
          type="tel"
          mask={{
            regex: format === 'expiration' ? '\\d{0,2}' : '[1-2]{1}\\d{0,3}',
            inputmode: 'tel',
          }}
          name={`${name}_year`}
          id="bday-year"
          label={format === 'expiration' ? __('!placeholder:yy') : __('!placeholder:yyyy')}
          value={year}
          ref={(e: any) => {
            inputYear.current = e;
            if (register) {
              register(e);
            }
          }}
          onChange={setYear}
          onActiveChange={(event) => {
            updateActive(event, 'year');
          }}
          readOnlyField={readOnlyField}
        />
      </span>
      <input type="hidden" value={formattedValue} name={name} id={id} ref={register} />
    </span>
  );
};

export const InputDate: StyledComponent<InputDateProps, {}, typeof InputDateRaw> = styled(
  InputDateRaw
)`
  position: relative;
  display: inline-block;
  width: 100%;
  border-radius: ${sizes.borderRadius};
  padding-top: calc(${sizes.padding} + 5px);
  font-weight: ${fontFamily.primary.black.fontWeight};

  .top-label {
    position: absolute;
    top: 0s;
    display: block;
    width: 100%;
    text-transform: uppercase;
    transform-origin: top left;
    transition: all 400ms;
  }

  .parts-wrapper {
    display: flex;
  }

  .day-part,
  .month-part {
    flex: 2;
    padding-right: 5px;
  }

  .year-part {
    flex: 6;
  }

  &.active {
    .top-label {
      transform: translateY(-5px);
    }
  }

  &.invalid {
    .top-label,
    .label {
      color: ${colorPalette.danger.active};
    }

    .field {
      &:before {
        background-color: ${colorPalette.danger.active};
      }
    }
  }
`;
